import { z } from "zod";
import React, { useEffect } from "react";
import { Script, type GetServerDataProps } from "gatsby";

import type { WithContext, AggregateRating } from "schema-dts";

import { parseGid } from "@shopify/hydrogen";

import { useLocation } from "@reach/router";

import { MetaComponent, type ImageProps } from "@bared/ui";

import {
  ProductSchema,
  filterProjection,
  TemplateProductSchema,
  type ShopifyType,
  SettingSizeGuideSchema,
  SettingBiomechanicalFootbedSchema,
  SettingCustomisableFitSchema,
} from "@bared/groq";

import type { ProductVariantFragment } from "types/storefront.generated";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { ErrorPage } from "~/components/Error/Error";
import { Product } from "~/components/Product/Product";
import { GiftCard } from "~/components/Product/GiftCard";
import { Accessory } from "~/components/Product/Accessory";
import Upsell from "~/components/Product/Upsell/Upsell";
import { ReviewsSentimentWidget } from "~/components/ReviewsSentimentWidget";

import { useSale } from "~/hooks/useSale";
import { useSite } from "~/hooks/useSite";
import { useTrendii } from "~/hooks/useTrendii";
import { useTripleWhale } from "~/hooks/useTripleWhale";
import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";
import { useUpdateRecentlyViewed } from "~/hooks/useRecentlyViewed";

import { ReviewsProvider } from "~/providers/ReviewsProvider";
import { ProductProvider as LegacyProductProvider } from "~/providers/ProductProvider";

import { mapToEdges } from "~/lib/sanity";
import { contextFromServerDataProps } from "~/lib/server";
import { seoConfigForPage, getSeoMeta, type SeoSchema } from "~/lib/seo";

import {
  PRODUCT_BY_HANDLE_QUERY,
  PRODUCT_RECOMMENDATIONS_QUERY,
} from "~/graphql/products";

import { Swatches } from "./Swatches";
import { AddToCartStack } from "./AddToCartStack";
import { AsSeenOnSection } from "./AsSeenOnSection";
import { ProductProvider } from "./ProductProvider";
import { HowToStyleSection } from "./HowToStyleSection";
import { ProductImageGallery } from "./ProductImageGallery";
import { ProductRecommendations } from "./ProductRecommendations";

const productTemplateQuery = filterProjection(
  TemplateProductSchema,
  `_type == "templateProduct"`,
);

const productQuery = filterProjection(
  ProductSchema,
  `_type == "product" && shopify.shopifyHandle == $handle && i18n_lang == $locale`,
);

const sizeGuideQuery = filterProjection(
  SettingSizeGuideSchema,
  `_type == "settingSizeGuide"`,
);

const biomechanicalFootbedQuery = filterProjection(
  SettingBiomechanicalFootbedSchema,
  `_type == "settingBiomechanicalFootbed"`,
);

const customisableFitQuery = filterProjection(
  SettingCustomisableFitSchema,
  `_type == "settingCustomisableFit"`,
);

const PRODUCT_UPSELL_QUERY = /* GraphQL */ `
  #graphql
  query ProductUpsell($id: ID!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    product(id: $id) {
      id
      handle
      title
      featuredImage {
        url
      }
      variants(first: 10) {
        edges {
          node {
            id
            sku
            title
            price {
              amount
              currencyCode
            }
            selectedOptions {
              name
              value
            }
            availableForSale
          }
        }
      }
    }
  }
` as const;

const PRODUCT_COLOUR_VARIANTS_QUERY = /* GraphQL */ `
  #graphql
  query getProducts(
    $filters: [ProductFilter!]
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collectionByHandle(handle: "internal-use-all-shoes") {
      handle
      id
      products(first: 200, filters: $filters) {
        nodes {
          handle
          tags
          availableForSale
          featuredImage {
            url
            height
            width
          }
          styleNameMetafield: metafield(
            namespace: "custom"
            key: "netsuite_style_name"
          ) {
            key
            namespace
            value
          }
          statusMetafield: metafield(
            namespace: "custom"
            key: "netsuite_status"
          ) {
            value
          }
          excludeSites: metafield(namespace: "custom", key: "exclude_sites") {
            references(first: 10) {
              nodes {
                __typename
                ... on Metaobject {
                  handle
                }
              }
            }
          }
          variants(first: 1) {
            nodes {
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;

// Types for product media returned from server.
type ProductMediaImage = { type: "image" } & ImageProps;
type ProductMediaVideo = {
  type: "video";
  coverImage: ImageProps;
  videoUrl: string;
};
type ProductMedia = ProductMediaImage | ProductMediaVideo;
type AsSeenOnMedia = ImageProps & { handle: string | null };

export type ProductVariant = Omit<
  ProductVariantFragment,
  "storeAvailability"
> & {
  /** Resolved colour of the variant */
  colour: string | null;
  /** Resolved size of the variant */
  size: string | null;
  /** Label for the variant selector */
  label: string;
  /** Status for the variant */
  status:
    | "PRE_ORDER"
    | "NOTIFY"
    | "UNAVAILABLE"
    | "AVAILABLE"
    | "LOW_STOCK"
    | "FINAL_SALE";
  /** Store availability without the "nodes" part */
  storeAvailability: ProductVariantFragment["storeAvailability"]["nodes"];
};

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const {
    respond,
    notFound,
    groq,
    locale,
    urlContext,
    storefront,
    site,
    sectionResolver,
    productCardResolver,
    googleAnalyticsItemResolver,
    sizeGuide: sizeGuideBasic,
  } = context;

  // Resolve handle.
  const handle = props.params?.["handle"];
  if ("string" !== typeof handle) throw new Error("No handle resolved.");

  // Fetch product from sanity and shopify.
  const [
    pageDefault,
    pageLocalised,
    template,
    shopifyResult,
    sizeGuide,
    biomechanicalFootbedSheetContents,
    customisableFitSheetContents,
  ] = await Promise.all([
    groq(productQuery, {
      handle,
      locale: null,
    }),
    groq(productQuery, { handle, locale: locale.identifier }),
    groq(productTemplateQuery),
    storefront.query(PRODUCT_BY_HANDLE_QUERY, { variables: { handle } }),
    groq(sizeGuideQuery),
    groq(biomechanicalFootbedQuery),
    groq(customisableFitQuery),
  ]);

  // Get shopify product from result.
  const shopifyProduct = shopifyResult.product;

  // Get style name from product.
  const styleName = shopifyProduct?.styleNameMetafield?.value;

  // Pick a page.
  const page = pageLocalised ?? pageDefault;

  // Page not found in Sanity or Shopify, return 404.
  if (!page || !shopifyProduct || !template) return notFound();

  // Has this product been hidden using the "Exclude sites" metafield?
  const productIsExcluded =
    !!shopifyProduct.excludeSites?.references?.nodes.find(
      (node) => "Metaobject" === node.__typename && site.handle === node.handle,
    );

  // If product excluded, return 404.
  if (productIsExcluded) return notFound();

  // Resolve sibling products.
  const colourVariants = await (async () => {
    // No style name.
    if (!styleName) return [];

    // Get variants from storefront.
    const res = await storefront.query(PRODUCT_COLOUR_VARIANTS_QUERY, {
      variables: {
        filters: [
          // Products with the same style name.
          {
            productMetafield: {
              key: "netsuite_style_name",
              namespace: "custom",
              value: styleName,
            },
          },
        ],
      },
    });

    // No collection found.
    if (!res.collectionByHandle) return [];

    // Return variants which are either available, or of "Seasonal" or "Core" status.
    return res.collectionByHandle.products.nodes
      .filter((product) => {
        // Has this product been hidden using the "Exclude sites" metafield?
        if (
          !!product.excludeSites?.references?.nodes.find(
            (node) =>
              "Metaobject" === node.__typename && site.handle === node.handle,
          )
        ) {
          return false;
        }

        // Get metafield values.
        const productStatus = product.statusMetafield?.value ?? null;
        const productStyleName = product.styleNameMetafield?.value ?? null;

        /**
         * These filters are technically in the storefront query "filters" attribute, but because these
         * filters are configured with the Shopify Search & Filter app, there is a chance that they
         * could get broken. Here, we are doing the same checks again just in case the query
         * ignores one of the filters.
         */
        const inQueryFilters = styleName === productStyleName;

        /**
         * These filters cannot be included in the storefront query because "filters" does not support OR.
         */
        const postQueryFilters =
          product.availableForSale === true ||
          productStatus === "Seasonal" ||
          productStatus === "Core";

        // Return only products which match both sets of filters.
        return !!inQueryFilters && !!postQueryFilters;
      })
      .map((product) => ({
        siblingHandle: product.handle,
        siblingColour: product.variants.nodes[0].selectedOptions.find(
          (option) =>
            option?.name?.includes("Color") || option?.name?.includes("Colour"),
        )?.value,
        image: {
          source: "shopify",
          data: {
            url: product.featuredImage?.url,
            height: product.featuredImage?.height,
            width: product.featuredImage?.width,
          },
        },
      }));
  })();

  // Fetch product recommendations from shopify.
  const productRecommendations = await (async () => {
    const shopifyData = await storefront.query(PRODUCT_RECOMMENDATIONS_QUERY, {
      variables: { productId: shopifyProduct.id },
    });

    if (!shopifyData.productRecommendations) return [];

    return shopifyData.productRecommendations
      .filter((shopifyProduct) => {
        // Has this product been hidden using the "Exclude sites" metafield?
        if (
          !!shopifyProduct.excludeSites?.references?.nodes.find(
            (node) =>
              "Metaobject" === node.__typename && site.handle === node.handle,
          )
        ) {
          return false;
        }

        return true;
      })
      .map((shopifyProduct) => productCardResolver.resolve({ shopifyProduct }));
  })();

  // Resolve shoe care upsell product id from metafield.
  const shoeCareUpsellProductIds = shopifyProduct.shoeCareUpsell
    ? (JSON.parse(shopifyProduct.shoeCareUpsell?.value) as string[])
    : [];

  // Resolve shoe care products from storefront.
  const shoeCareUpsellProducts = (
    await Promise.all(
      shoeCareUpsellProductIds.map((id) =>
        storefront
          .query(PRODUCT_UPSELL_QUERY, { variables: { id } })
          .then((res) => res.product),
      ),
    )
  )
    .filter(
      (product) =>
        product && !!product.featuredImage && !!product.variants.edges[0],
    )
    .filter(Boolean);

  // Resolve flexible sections in the size guide template.
  const sizeGuideSheetContents = sizeGuide
    ? {
        ...sizeGuide,
        sizingAdvice: shopifyProduct.sizingAdvice
          ? shopifyProduct.sizingAdvice.value
          : null,
        flexibleSections: sizeGuide.flexibleSections
          ? await sectionResolver.resolve({
              sanitySections: sizeGuide.flexibleSections,
            })
          : null,
      }
    : null;

  const templateType = (() => {
    if (shopifyProduct.tags?.includes("type:giftcard")) {
      return "giftcard";
    }

    if (shopifyProduct.tags?.includes("type:accessory")) {
      return "accessory";
    }

    return "product";
  })();

  // The shopifyRaw attribute currently needs sanitizing, because it contains a huge amount of unnecessary data.
  if (page.shopify)
    page.shopify = sanitizeSanityShopifyData(page.shopify, shopifyProduct);

  // Compute aggregate rating for product.
  const aggregateRating: WithContext<AggregateRating> | null =
    !!shopifyProduct.reviewsCount && !!shopifyProduct.reviewsRating
      ? {
          "@context": "https://schema.org",
          "@type": "AggregateRating",
          ratingValue: shopifyProduct.reviewsRating.value,
          ratingCount: parseInt(shopifyProduct.reviewsCount.value),
        }
      : null;

  /**
   * Construct a JSON-LD schema for product.
   * @see https://developers.google.com/search/docs/appearance/structured-data/product
   */
  const productSchema: SeoSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: shopifyProduct.title,
    url: urlContext.canonicalUrl,
    gtin13: parseGid(shopifyProduct.id).id,
    description: shopifyProduct.seo.description ?? undefined,
    image: shopifyProduct.featuredImage
      ? {
          "@type": "ImageObject",
          url: shopifyProduct.featuredImage.openGraphUrl,
          description: shopifyProduct.seo.description ?? undefined,
        }
      : undefined,
    brand: { "@type": "Brand", name: "Bared Footwear" },
    sku: shopifyProduct.variants.edges[0]?.node.sku ?? undefined,
    offers: {
      "@type": "AggregateOffer",
      lowPrice: shopifyProduct.priceRange.minVariantPrice.amount,
      highPrice: shopifyProduct.priceRange.maxVariantPrice.amount,
      priceCurrency: shopifyProduct.priceRange.maxVariantPrice.currencyCode,
      offerCount: shopifyProduct.variants.edges.length,
      offers: shopifyProduct.variants.edges.map(({ node }) => ({
        "@type": "Offer",
        price: node.price.amount,
        priceCurrency: node.price.currencyCode,
        sku: node.sku ?? undefined,
        availability: "https://schema.org/InStock",
        url: `${urlContext.canonicalUrl}?variant=${parseGid(node.id).id}`,
      })),
    },
    ...(aggregateRating ? { aggregateRating } : {}),
  };

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    schemas: [productSchema],
    sanityMetadata: page.metadata,
    description: shopifyProduct.seo.description,
    title: shopifyProduct.seo.title ?? shopifyProduct.title,
    media: shopifyProduct.featuredImage?.openGraphUrl,
  });

  // Resolve description calloout content from the metafield
  const descriptionCalloutContent = shopifyProduct.descriptionCallout
    ? JSON.parse(shopifyProduct.descriptionCallout.value)
    : null;

  // Resolve how to style content from the metafield
  const howToStyleContent = shopifyProduct.howToStyleContent
    ? JSON.parse(shopifyProduct.howToStyleContent.value)
    : null;

  // Resolve sustainable feature from the product metafield
  const sustainableFeatures = shopifyProduct.sustainableFeatures?.value && {
    id: shopifyProduct.sustainableFeatures?.key ?? "sustainable_features",
    title: "Sustainable Features",
    value: shopifyProduct.sustainableFeatures.value,
  };

  // Resolve shoe care guide from the product metafield
  const shoeCareGuide = shopifyProduct.shoeCareGuide?.value && {
    id: shopifyProduct.shoeCareGuide?.key ?? "shoe_care_guide",
    title: "Shoe Care",
    value: shopifyProduct.shoeCareGuide.value,
  };

  // Resolve how to style images from the metafield
  const howToStyleImages: ImageProps[] = shopifyProduct.howToStyleImages
    ?.references
    ? shopifyProduct.howToStyleImages.references.nodes
        .filter(({ image }) => !!image)
        .map(({ alt, image }) => {
          // Should never reach here, as filtered above.
          if (!image) throw new Error("No image on metafield.");

          return {
            data: image,
            alt: alt ?? "",
            source: "shopify",
          };
        })
    : [];

  // Resolve as seen on images from the metafield
  const asSeenOnImages: AsSeenOnMedia[] = shopifyProduct.asSeenOnImages
    ?.references
    ? shopifyProduct.asSeenOnImages.references.nodes
        .filter(({ image }) => !!image?.reference?.image)
        .map(({ image, socialHandle }) => {
          if (!image?.reference?.image)
            throw new Error("No image on metafield.");

          return {
            source: "shopify",
            data: image.reference.image,
            handle: !socialHandle ? null : "@" + socialHandle.value,
          };
        })
    : [];

  // Extract video URL from Sanity.
  const videoUrl = page.video?.url ?? null;

  // Start product media with the images from shopify.
  let productMedia = shopifyProduct.images.nodes.map((data): ProductMedia => {
    /**
     * Shopify product images which have the substring "_cover" in their name are
     * the cover image for a video, stored in Sanity.
     * @todo Store actual videos in Shopify, not just cover images.
     */
    if (videoUrl && data.url.toLowerCase().includes("_cover")) {
      return {
        videoUrl,
        type: "video",
        coverImage: { source: "shopify", data, alt: shopifyProduct.title },
      };
    }

    return {
      data,
      type: "image",
      source: "shopify",
      alt: shopifyProduct.title,
    };
  });

  // Get the products name from the relevant products metafield value
  const productName = !!shopifyProduct.styleNameMetafield?.value
    ? shopifyProduct.styleNameMetafield.value
    : null;

  // Get the products gender from the products metafield
  const productGender = (() => {
    if (!shopifyProduct.genderMetafield?.value) return null;
    const parse = productGenderSchema.safeParse(
      shopifyProduct.genderMetafield.value,
    );
    return parse.success ? parse.data : null;
  })();

  // Get the product status from the product metafield
  const productStatus = !!shopifyProduct.statusMetafield?.value
    ? shopifyProduct.statusMetafield.value
    : null;

  // Enable the "Notify when back in stock" functionality.
  const enableNotify = productStatus
    ? ["Core", "Seasonal"].includes(productStatus)
    : false;

  // Get primary collection from the products metafield
  const primaryCollection = !!shopifyProduct.primaryCollection
    ? shopifyProduct.primaryCollection.reference
    : null;

  // Determine if the footbed image should be hidden.
  const hideFootbedImage = !!shopifyProduct.hideFootbedImage?.value
    ? JSON.parse(shopifyProduct.hideFootbedImage.value)
    : false;

  // Resolve a footbed image asset for the gender.
  const footbedImageAsset =
    "Womens" === productGender
      ? template.womensFootbedImage?.asset
      : "Mens" === productGender
        ? template.mensFootbedImage?.asset
        : null;

  // If we have a footbed image, splice that into 3rd position.
  if (footbedImageAsset && !hideFootbedImage) {
    productMedia.splice(2, 0, {
      type: "image",
      source: "sanity",
      data: footbedImageAsset,
      alt: "Image of footbed",
    });
  }

  // Resolve navigation root path.
  const navigationRootPath =
    productGender === "Mens"
      ? "/men"
      : productGender === "Womens"
        ? "/women"
        : "/";

  // Resolve a plain array of variant SKUs.
  const variantSkus = shopifyProduct.variants.edges.reduce((acc, cur) => {
    return [...acc, ...(cur.node.sku ? [cur.node.sku] : [])];
  }, [] as string[]);

  /**
   * Determine the threshold for "low stock" messaging.
   * If a variant has less or equal to this amount, it will be labelled as low stock.
   */
  const lowStockThreshold = 3;

  // Determine if the product is final sale.
  const isFinalSale = !!shopifyProduct.finalSale?.value
    ? JSON.parse(shopifyProduct.finalSale.value)
    : false;

  // Determine if the product has a fit kit included.
  const isFitKitIncluded = !!shopifyProduct.fitKitIncluded?.value
    ? JSON.parse(shopifyProduct.fitKitIncluded.value)
    : false;

  // Resolve the size guide for this gender.
  const sizeGuideForGender =
    "Womens" === productGender
      ? sizeGuideBasic.women
      : "Mens" === productGender
        ? sizeGuideBasic.men
        : null;

  // Resolve an array of variants for the product.
  const productVariants: ProductVariant[] = shopifyProduct.variants.edges.map(
    ({ node: productVariant }) => {
      // Resolve colour of the variant.
      const colour =
        productVariant.selectedOptions.find((option) =>
          ["color", "colour"].includes(option.name.toLowerCase()),
        )?.value ?? null;

      // Resolve size of the variant.
      const size =
        productVariant.selectedOptions.find(
          (option) => "size" === option.name.toLowerCase(),
        )?.value ?? null;

      /**
       * Determine if this variant is on preorder by looking for a tag in the
       * format preorder:42 where 42 is the size.
       */
      const isPreOrder =
        !!size &&
        !!shopifyProduct.tags.find(
          (tag) => `preorder:${size}` === tag.toLowerCase(),
        );

      // Determine the quantity available for this variant.
      const quantityAvailable = !!productVariant.quantityAvailable
        ? productVariant.quantityAvailable
        : 0;

      // Resolve a status for the "Add to cart" button.
      const status: ProductVariant["status"] = productVariant.availableForSale
        ? isPreOrder
          ? "PRE_ORDER"
          : isFinalSale
            ? "FINAL_SALE"
            : quantityAvailable <= lowStockThreshold
              ? "LOW_STOCK"
              : "AVAILABLE"
        : enableNotify
          ? "NOTIFY"
          : "UNAVAILABLE";

      // Resolve a label with a size conversion if possible.
      const label = (() => {
        // If we don't have a size, output the variant title.
        if (!size) return productVariant.title;

        // We don't have a size guide for this geneder, return just the size.
        if (!sizeGuideForGender) return size;

        // Try to resolve a US size from the size guide.
        const usSize = sizeGuideForGender.find(
          (row) => row.bared === size,
        )?.us_au;

        // Could not resolve a US size, return just the size.
        if (!usSize) return size;

        // Return size with US conversion appended.
        return `${size} (US ${usSize})`;
      })();

      return {
        ...productVariant,
        size,
        label,
        colour,
        status,
        storeAvailability: productVariant.storeAvailability.nodes,
      };
    },
  );

  // Resolve a badge.
  const badge = context.productBadgeResolver.resolve({ shopifyProduct });

  const { price, compareAtPrice } = context.productPriceResolver.resolve({
    shopifyProduct,
  });

  const googleAnalyticsItem =
    googleAnalyticsItemResolver.resolveFromShopifyProduct({
      shopifyProduct,
      price,
      compareAtPrice,
    });

  // Respond with server data.
  return respond({
    seo,
    page,
    badge,
    descriptionCalloutContent,
    template,
    isFinalSale,
    isFitKitIncluded,
    variantSkus,
    templateType,
    productMedia,
    shopifyProduct,
    productVariants,
    navigationRootPath,
    colourVariants,
    productName,
    productGender,
    productStatus,
    enableNotify,
    productRecommendations,
    primaryCollection,
    shoeCareGuide,
    shoeCareUpsellProducts,
    howToStyleContent,
    howToStyleImages,
    asSeenOnImages,
    sustainableFeatures,
    sizeGuideSheetContents,
    biomechanicalFootbedSheetContents,
    customisableFitSheetContents,
    price,
    compareAtPrice,
    googleAnalyticsItem,
  } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export type ServerDataSuccess = Extract<ServerData, { status: 200 }>;

export default function ProductPage(props: PageProps<ServerData>) {
  const { serverData } = props;

  /**
   * The key prop forces the component to remount when the URI changes rather than just passing
   * through the updated props. We have deep components which do not re-render properly when
   * their props change, so currently remounting is our only option to remain consistent.
   * @see https://bared.atlassian.net/browse/ECOM-2579
   * @see https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1715903163431309
   */
  if (200 === serverData.status) {
    return (
      <>
        <ProductProvider serverData={serverData} key={props.uri}>
          <ReviewsProvider>
            <LegacyProductProvider stub={serverData.page} queryType="PDP">
              <ProductPageInner {...props} />
            </LegacyProductProvider>
          </ReviewsProvider>
        </ProductProvider>
        <Script
          strategy="idle"
          src="https://widget.reviews.io/rating-snippet/dist.js"
        />
      </>
    );
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

function ProductPageInner({ serverData }: PageProps<ServerDataSuccess>) {
  const site = useSite();
  const trendii = useTrendii();
  const location = useLocation();
  const tripleWhale = useTripleWhale();
  const googleTagManager = useGoogleTagManager();

  // Send tracking events when server data changes.
  useEffect(() => {
    const { shopifyProduct } = serverData;

    const trendiiProductData = {
      id: shopifyProduct.id,
      title: shopifyProduct.title,
      sku: shopifyProduct.variants.edges[0]?.node.sku,
      price: shopifyProduct.priceRange.maxVariantPrice.amount,
      currency: shopifyProduct.priceRange.maxVariantPrice.currencyCode,
    };
    trendii.trackPageLoad(trendiiProductData);

    tripleWhale.trackPageLoad("Product");

    googleTagManager.push({
      event: "view_item",
      properties: {
        items: [serverData.googleAnalyticsItem],
      },
    });
  }, [googleTagManager, tripleWhale, trendii, serverData, location]);

  // For some reason the content prop on the product component requires an edges array but
  // only ever accesses the first item.
  // @todo Remove when product component does not expect edges.
  const pageEdges = React.useMemo(
    () =>
      mapToEdges(
        200 === serverData.status && serverData.page ? [serverData.page] : [],
      ),
    [serverData],
  );

  /** @todo Fully move this to the server. */
  const saleContext = useSale();

  const updateRecentlyViewed = useUpdateRecentlyViewed();

  // Update recently viewed cookie.
  useEffect(() => {
    updateRecentlyViewed({ productHandle: serverData.shopifyProduct.handle });
  }, [serverData, updateRecentlyViewed]);

  if ("giftcard" === serverData.templateType) {
    return (
      <GiftCard
        location={location}
        template={serverData.template}
        content={pageEdges}
        productName={serverData.productName}
        productGender={serverData.productGender}
      />
    );
  }

  if ("accessory" === serverData.templateType) {
    return (
      <Accessory
        location={location}
        template={serverData.template}
        content={pageEdges}
        productName={serverData.productName}
      />
    );
  }

  // Render the shoe care section.
  const shoeCareSection =
    0 < serverData.shoeCareUpsellProducts.length ? (
      <div id="shoe-care" className="mb-midi grid grid-cols-2">
        {serverData.shoeCareUpsellProducts.map((product) => (
          <Upsell
            key={product.id}
            handle={product.handle}
            imageSrc={product.featuredImage.url}
            title={product.title}
            variant={product.variants.edges[0].node}
          />
        ))}
      </div>
    ) : null;

  // Render reviews sentiment widget.
  const reviewsSummary = (
    <ReviewsSentimentWidget
      productHandle={serverData.shopifyProduct.handle}
      variantSkus={serverData.variantSkus}
    />
  );

  return (
    <Product
      location={location}
      template={serverData.template}
      primaryCollection={serverData.primaryCollection}
      productName={serverData.productName}
      productGender={serverData.productGender}
      productStatus={serverData.productStatus}
      content={pageEdges}
      reviewsSummary={reviewsSummary}
      imageGallery={
        <ProductImageGallery
          howToStyle={!!serverData.howToStyleContent}
          modalId="product-image-gallery"
          badge={serverData.badge}
          productMedia={serverData.productMedia}
          key={`ProductImageGallery-${serverData.shopifyProduct.handle}`} // Ensure that scrollable nodes remount when navigating between products.
        />
      }
      shoeCareSection={[shoeCareSection].filter(Boolean)}
      howToStyleSection={
        0 < serverData.howToStyleImages.length &&
        !!serverData.howToStyleContent ? (
          <HowToStyleSection
            howToStyleImages={serverData.howToStyleImages}
            howToStyleContent={serverData.howToStyleContent}
            key={`HowToStyleSection-${serverData.shopifyProduct.handle}`} // Ensure that scrollable nodes remount when navigating between products.
          />
        ) : null
      }
      shoeCareGuide={serverData.shoeCareGuide}
      sustainableFeatures={serverData.sustainableFeatures}
      descriptionCallout={serverData.descriptionCalloutContent}
      sizingAdvice={serverData.sizeGuideSheetContents?.sizingAdvice}
      productRecommendations={
        !!serverData.productRecommendations &&
        0 < serverData.productRecommendations.length ? (
          <ProductRecommendations
            productRecommendations={serverData.productRecommendations}
            currentProductHandle={serverData.shopifyProduct.handle}
          />
        ) : null
      }
      asSeenOnSection={
        serverData.asSeenOnImages.length >= 4 ? (
          <AsSeenOnSection
            asSeenOnImages={serverData.asSeenOnImages}
            key={`AsSeenOnSection-${serverData.shopifyProduct.handle}`}
          />
        ) : null
      }
      swatches={
        serverData.colourVariants.length >= 1 ? (
          <Swatches
            colourVariants={serverData.colourVariants}
            handle={serverData.shopifyProduct.handle}
          />
        ) : null
      }
      // Render sibling colours which then will be used as style name for reviews
      siblingColours={serverData.colourVariants
        .map(({ siblingColour }) => siblingColour)
        .filter(Boolean)}
    >
      <AddToCartStack
        gender={serverData.productGender}
        enableFloatingBar
        countryCode={site.countryCode}
        sheetFooterContent={serverData.template.sheetFooterContent}
        sizeGuide={serverData.sizeGuideSheetContents}
        biomechanicalFootbed={serverData.biomechanicalFootbedSheetContents}
        customisableFit={serverData.customisableFitSheetContents}
        isFitKitIncluded={serverData.isFitKitIncluded}
      />
    </Product>
  );
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;

  return (
    <>
      <link
        rel="stylesheet"
        href="https://widget.reviews.io/rating-snippet/dist.css"
      />
      <MetaComponent
        meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
      />
    </>
  );
}

/**
 * Currently, the shopifyRaw field includes a huge reviews metafield containing HTML, which breaks
 * page data responses. This function removes that metafield from the object.
 *
 * It also renames the attributes to omit the shopify prefix, which for some reason all the templates
 * are expecting. So for example, the `shopifyRaw` attribute becomes `raw`.
 *
 * @todo Remove this when the templates no longer use shopifyRaw, and update the Sanity query to
 * exclude that field entirely. Even better, remove the shopifyRaw field from Sanity.
 */
export function sanitizeSanityShopifyData<T extends ShopifyType>(
  shopify: T,
  shopifyProduct?: ServerDataSuccess["shopifyProduct"],
): T {
  if (!shopify.shopifyRaw) return shopify;

  const parsedShopifyRaw = JSON.parse(shopify.shopifyRaw) as any;

  const sanitizedShopifyRaw = {
    ...parsedShopifyRaw,
    metafields: (parsedShopifyRaw.metafields as { key: string }[]).filter(
      (metafield) => "static_product_widget" !== metafield.key,
    ),
  };

  /**
   * If a proper shopify product has been provided, we will override some of the sanity data to prevent a "flash" of incorrect
   * pricing on the page. Soon, we will drop sanity data entirely when the components are running purely from the shopify
   * product data provided by the server, which uses the market context.
   */
  if (shopifyProduct) {
    sanitizedShopifyRaw.priceRange = shopifyProduct.priceRange;

    sanitizedShopifyRaw.variants = sanitizedShopifyRaw.variants.map(
      (variant: any) => {
        const actualVariant = shopifyProduct.variants.edges.find(
          (edge) => edge.node.sku === variant.sku,
        )?.node;
        if (!actualVariant) return variant;

        return {
          ...variant,
          priceV2: actualVariant.price.amount,
          compareAtPriceV2: actualVariant.compareAtPrice?.amount ?? null,
        };
      },
    );
  }

  return Object.entries(shopify).reduce((acc, [key, _value]) => {
    // Get the key without the shopify prefix.
    let nakedKey = key.replace("shopify", "");
    nakedKey = nakedKey.charAt(0).toLocaleLowerCase() + nakedKey.slice(1);

    const value =
      "shopifyRaw" === key ? JSON.stringify(sanitizedShopifyRaw) : _value;

    return {
      ...acc,
      [key]: value,
      [nakedKey]: value,
    };
  }, {} as T);
}

const productGenderSchema = z.union([z.literal("Mens"), z.literal("Womens")]);
